import { useReactiveVar } from '@apollo/client/react';
import {
  fetchDevices,
  fetchMFAPreference,
  FetchMFAPreferenceOutput,
  fetchUserAttributes,
  forgetDevice,
  resetPassword,
  updateMFAPreference
} from '@aws-amplify/auth';
import { QrCodeOutlined } from '@mui/icons-material';
import { Button, Divider, Modal, Typography } from '@mui/material';
import TwoFactorAuthentication from 'components/Modal/variants/TwoFactorAuthentication';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { authVar } from 'state/vars';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { createMyProfileTabs } from '../myProfileTabsConst';

const Security: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const [isTwoFactorAuthenticationModalOpen, setIsTwoFactorAuthenticationModalOpen] = useState(false);
  const myProfileTabs = createMyProfileTabs(translation, user?.isEntraLess || false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const [mfaPreference, setMfaPreference] = useState<FetchMFAPreferenceOutput>();

  useEffect(() => {
    const getMFAStatus = async () => {
      try {
        const preferredMFA = await fetchMFAPreference();
        setMfaPreference(preferredMFA);
      } catch (err) {
        console.error(err);
        return null;
      }
    };
    getMFAStatus();
  }, [isTwoFactorAuthenticationModalOpen]);

  const handleForgotPassword = async () => {
    try {
      const userAttributes = await fetchUserAttributes();
      if (userAttributes.email) await resetPassword({ username: userAttributes.email });
    } catch (err) {
      handleFetchError('' + err);
    }
  };

  const handleOpenTwoFactorAuthenticationModal = () => setIsTwoFactorAuthenticationModalOpen(true);
  const handleCloseTwoFactorAuthenticationModal = () => setIsTwoFactorAuthenticationModalOpen(false);

  const handleEnableTwoFactorAuthentication = async () => {
    handleOpenTwoFactorAuthenticationModal();
  };

  const handleDisableTwoFactorAuthentication = async () => {
    try {
      const trustedDevices = await fetchDevices();
      const forgotDevicePromises = trustedDevices.map((device) => forgetDevice({ device }));
      await Promise.all(forgotDevicePromises);
      const trustedDevicesAfterDelete = await fetchDevices();
      if (trustedDevicesAfterDelete.length === 0) {
        const result = await updateMFAPreference({ totp: 'DISABLED' });
        const preferredMFA = await fetchMFAPreference();
        setMfaPreference(preferredMFA);
      }
    } catch (err) {
      handleFetchError('' + err);
    }
  };

  return (
    <PageContent title={translation.my_device} titleId="location" tabs={myProfileTabs} id="access-app-page">
      <ComponentWrapper flexDirection="column" gap="32px" padding="32px 0">
        <ComponentWrapper flexDirection="column" gap="24px">
          <Typography variant="h5" color="textPrimary">
            {translation.password}
          </Typography>
          <Button
            size="large"
            color="primary"
            variant="outlined"
            style={{ width: 'fit-content', color: 'rgba(255, 255, 255, 0.87)' }}
            onClick={handleForgotPassword}
          >
            {translation.reset_password}
          </Button>
        </ComponentWrapper>
        <Divider />
        <ComponentWrapper flexDirection="column" gap="24px">
          <ComponentWrapper flexDirection="column">
            <Typography variant="h5" color="textPrimary">
              {translation.two_factor_authentication}
            </Typography>
            {!mfaPreference?.enabled && (
              <Typography variant="body1" color="textPrimary">
                {
                  translation.we_recommend_using_two_factor_authentication_to_put_an_extra_layer_of_security_on_your_account
                }
              </Typography>
            )}
          </ComponentWrapper>
          <ComponentWrapper flexDirection="row" alignItems="center">
            {mfaPreference?.enabled ? (
              <ComponentWrapper flexDirection="column" gap="24px">
                <ComponentWrapper
                  alignItems="center"
                  gap="16px"
                  padding="16px"
                  style={{ border: '1px solid rgba(68, 68, 68, 1)' }}
                >
                  <QrCodeOutlined style={{ color: 'rgba(204, 204, 0, 1)' }} />
                  <ComponentWrapper flexDirection="column">
                    <Typography variant="body1" color="textPrimary">
                      {translation.authenticator_app}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {translation.two_factor_authentication_is_set_up_with_an_app}
                    </Typography>
                  </ComponentWrapper>
                </ComponentWrapper>
                <Button
                  size="large"
                  color="error"
                  variant="outlined"
                  style={{ width: 'fit-content', color: 'rgba(244, 67, 54, 1)' }}
                  onClick={handleDisableTwoFactorAuthentication}
                >
                  {translation.disable}
                </Button>
              </ComponentWrapper>
            ) : (
              <Button
                size="large"
                color="primary"
                variant="outlined"
                style={{ width: 'fit-content', color: 'rgba(255, 255, 255, 0.87)' }}
                onClick={handleEnableTwoFactorAuthentication}
              >
                {translation.enable_two_factor_authentication}
              </Button>
            )}
          </ComponentWrapper>
        </ComponentWrapper>
        <Modal
          open={isTwoFactorAuthenticationModalOpen}
          onClose={handleCloseTwoFactorAuthenticationModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <TwoFactorAuthentication handleClose={handleCloseTwoFactorAuthenticationModal} />
        </Modal>
      </ComponentWrapper>
    </PageContent>
  );
};
export default Security;
